import { useCallback, useEffect, useRef, useState } from 'react'
import { BACKEND_APIS } from '@constants/routes'
import { queryOf } from './viewState'

/**
 * The data of "Last changes" (CU-869fn0gcp): one request for the whole picture (`GET /last-changes`), one for the
 * pushes of a pick-up or a cell (`GET /last-changes/pushes`). The server computes everything; this fetches and keeps
 * the last answer on screen while the next one arrives (`refetching`), as Activity's feed does. An aborted request
 * (a filter changed again) never lands.
 */
export const PUSHES_PAGE = 100

const message = e => (e?.status === 404 ? 'Last changes is not available on this server.' : e?.message || 'The activity could not be read.')

export function useLastChanges(view) {
	const key = view?.office ? queryOf(view) : null
	const [state, setState] = useState({ data: null, loading: !!key, refetching: false, error: null })
	const [tick, setTick] = useState(0)
	const controller = useRef(null)

	useEffect(() => {
		if (!key) return undefined
		controller.current?.abort()
		const ctl = new AbortController()
		controller.current = ctl
		setState(s => ({ data: s.data, loading: !s.data, refetching: !!s.data, error: null }))
		fetch(`${BACKEND_APIS.GENERAL_REPRICING_API}/last-changes?${key}`, { credentials: 'include', signal: ctl.signal })
			.then(async res => {
				const body = await res.json().catch(() => null)
				if (!res.ok) { const e = new Error(body?.error || `HTTP ${res.status}`); e.status = res.status; throw e }
				return body
			})
			.then(data => { if (!ctl.signal.aborted) setState({ data, loading: false, refetching: false, error: null }) })
			.catch(e => { if (!ctl.signal.aborted) setState(s => ({ data: s.data, loading: false, refetching: false, error: message(e) })) })
		return () => ctl.abort()
	}, [key, tick])

	const reload = useCallback(() => setTick(t => t + 1), [])
	return { ...state, reload }
}

/**
 * The pushes of one pick-up (every ACRISS), one cell, or — `pickup` null — one ACRISS over every date of the sample
 * (the ACRISS detail's curve), newest first, up to `PUSHES_PAGE` rows per request.
 */
export function fetchPushes(view, { pickup = null, sipp = null, page = 1 }, signal) {
	const q = new URLSearchParams(queryOf(view))
	if (pickup) q.set('pickup', pickup)
	if (sipp) q.set('sipp', sipp)
	q.set('page', String(page))
	q.set('page_size', String(PUSHES_PAGE))
	return fetch(`${BACKEND_APIS.GENERAL_REPRICING_API}/last-changes/pushes?${q.toString()}`, { credentials: 'include', signal })
		.then(async res => {
			const body = await res.json().catch(() => null)
			if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`)
			return body
		})
}

const MAX_CURVE_PAGES = 10

/**
 * Every push of one ACRISS over the sample (the curve needs all passes): pages of `PUSHES_PAGE` until `total`, at most
 * `MAX_CURVE_PAGES`. Returns `total` too: the caller drops a pass cut by the cap (`passCurve.completePasses`).
 */
export async function fetchAcrissPushes(view, sipp, signal) {
	const items = []
	let total = 0
	for (let page = 1; page <= MAX_CURVE_PAGES; page += 1) {
		const b = await fetchPushes(view, { sipp, page }, signal)
		items.push(...(b.items || []))
		total = b.total || 0
		if (items.length >= total || !(b.items || []).length) break
	}
	return { items, total }
}

export function usePushes(view, sel) {
	const key = view?.office && sel?.pickup ? `${queryOf(view)}|${sel.pickup}|${sel.sipp || ''}` : null
	const [state, setState] = useState({ items: [], total: 0, counts: null, loading: false, error: null })
	useEffect(() => {
		if (!key) { setState({ items: [], total: 0, counts: null, loading: false, error: null }); return undefined }
		const ctl = new AbortController()
		setState(s => ({ ...s, loading: true, error: null }))
		fetchPushes(view, sel, ctl.signal)
			.then(b => { if (!ctl.signal.aborted) setState({ items: b.items || [], total: b.total || 0, counts: b.counts || null, loading: false, error: null }) })
			.catch(e => { if (!ctl.signal.aborted) setState({ items: [], total: 0, counts: null, loading: false, error: e?.message || 'The pushes could not be read.' }) })
		return () => ctl.abort()
	}, [key])  // eslint-disable-line react-hooks/exhaustive-deps
	return state
}

/** The Excel of the current view: posts the rows, downloads the file the server names. */
export async function downloadExcel(body) {
	const res = await fetch(`${BACKEND_APIS.GENERAL_REPRICING_API}/last-changes/excel`, {
		method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
	})
	if (!res.ok) {
		const err = await res.json().catch(() => ({}))
		throw new Error(err.error || 'Could not prepare the Excel.')
	}
	const name = (res.headers.get('content-disposition') || '').split('filename=')[1]?.replace(/"/g, '') || 'last-changes.xlsx'
	const blob = await res.blob()
	const a = document.createElement('a')
	a.href = window.URL.createObjectURL(blob)
	a.download = name
	document.body.appendChild(a)
	a.click()
	a.remove()
	return name
}
